import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";
import type { SSO_ERROR_CODES } from "@amtana/sso-contract";

// What an admin reads when a single sign-on step fails (packages/module ADR-001),
// in English and Spanish, read in the active language when the error shows.

const EN = {
  planRequiredTitle: "Single sign-on needs a higher plan",
  planRequiredDescription: "It is on the Enterprise plan.",
  notConnectedTitle: "No identity provider yet",
  notConnectedDescription: "Connect an identity provider first.",
  alreadyConnectedTitle: "An identity provider is already connected",
  alreadyConnectedDescription: "Remove it before you connect another one.",
  issuerUnreachableTitle: "Your identity provider didn't answer",
  issuerUnreachableDescription: "Check the issuer URL and try again.",
  publicDomainTitle: "That's a public email domain",
  publicDomainDescription: "Add your company's own domain.",
  domainTakenTitle: "That domain is already claimed",
  domainTakenDescription: "Another company on {product} has claimed it.",
  domainNotFoundTitle: "That domain isn't on this connection",
  domainNotFoundDescription: "Add it to the connection first.",
  notReadyTitle: "Single sign-on isn't ready yet",
  notReadyDescription: "Verify a domain and run a test sign-in first.",
  wrongDomainTitle: "That email isn't on this connection",
  wrongDomainDescription: "Use an email on one of the domains this connection signs in.",
  domainJoinUnavailableTitle: "Domain join needs a verified domain",
  domainJoinUnavailableDescription: "Verify at least one domain before turning domain join on.",
} as const;

const ES: Translation<typeof EN> = {
  planRequiredTitle: "El inicio de sesión único necesita un plan superior",
  planRequiredDescription: "Está en el plan Enterprise.",
  notConnectedTitle: "Aún no hay un proveedor de identidad",
  notConnectedDescription: "Conecta primero un proveedor de identidad.",
  alreadyConnectedTitle: "Ya hay un proveedor de identidad conectado",
  alreadyConnectedDescription: "Quítalo antes de conectar otro.",
  issuerUnreachableTitle: "Tu proveedor de identidad no respondió",
  issuerUnreachableDescription: "Revisa la URL del emisor e inténtalo de nuevo.",
  publicDomainTitle: "Ese es un dominio de correo público",
  publicDomainDescription: "Añade el dominio propio de tu empresa.",
  domainTakenTitle: "Ese dominio ya fue reclamado",
  domainTakenDescription: "Otra empresa en {product} lo reclamó.",
  domainNotFoundTitle: "Ese dominio no está en esta conexión",
  domainNotFoundDescription: "Añádelo primero a la conexión.",
  notReadyTitle: "El inicio de sesión único aún no está listo",
  notReadyDescription: "Verifica un dominio y haz una prueba de inicio de sesión primero.",
  wrongDomainTitle: "Ese correo no está en esta conexión",
  wrongDomainDescription: "Usa un correo de uno de los dominios con los que inicia sesión esta conexión.",
  domainJoinUnavailableTitle: "La unión por dominio necesita un dominio verificado",
  domainJoinUnavailableDescription: "Verifica al menos un dominio antes de activar la unión por dominio.",
};

export const SSO_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const SSO_ERROR_COPY = {
  sso_plan_required: {
    title: phrase(SSO_ERROR_CATALOGS, "planRequiredTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "planRequiredDescription"),
  },
  sso_not_connected: {
    title: phrase(SSO_ERROR_CATALOGS, "notConnectedTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "notConnectedDescription"),
  },
  sso_already_connected: {
    title: phrase(SSO_ERROR_CATALOGS, "alreadyConnectedTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "alreadyConnectedDescription"),
  },
  sso_issuer_unreachable: {
    title: phrase(SSO_ERROR_CATALOGS, "issuerUnreachableTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "issuerUnreachableDescription"),
  },
  sso_public_domain: {
    title: phrase(SSO_ERROR_CATALOGS, "publicDomainTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "publicDomainDescription"),
  },
  sso_domain_taken: {
    title: phrase(SSO_ERROR_CATALOGS, "domainTakenTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "domainTakenDescription"),
  },
  sso_domain_not_found: {
    title: phrase(SSO_ERROR_CATALOGS, "domainNotFoundTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "domainNotFoundDescription"),
  },
  sso_not_ready: {
    title: phrase(SSO_ERROR_CATALOGS, "notReadyTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "notReadyDescription"),
  },
  sso_wrong_domain: {
    title: phrase(SSO_ERROR_CATALOGS, "wrongDomainTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "wrongDomainDescription"),
  },
  sso_domain_join_unavailable: {
    title: phrase(SSO_ERROR_CATALOGS, "domainJoinUnavailableTitle"),
    description: phrase(SSO_ERROR_CATALOGS, "domainJoinUnavailableDescription"),
  },
} satisfies ErrorCopyFor<typeof SSO_ERROR_CODES>;
